import { FormEventHandler } from "react";

import { Grid, Stack } from "@mui/material";
import { CountryNames } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { COUNTRY_NAME_WITH_SHORT_CODE_OPTIONS } from "@/common/constants/countryOptions";
import { isEmailAndAllowPlus, isPhoneNumberValid, isRequired } from "@/common/helpers/validators";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormInfobox } from "@/forms-design-system/FormInfobox/FormInfobox";
import { FormPhoneInputField } from "@/forms-design-system/FormPhoneInputField";
import { FormSearchableSelectField } from "@/forms-design-system/FormSearchableSelectField";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";
import IdeaIcon from "@/icons/complete/idea.svg";

export interface SweepstakesFreeEntryFields {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  region: string;
}

export const FREE_ENTRY_FORM_ID = "sweepstakes-free-entry-form";

const OPAQUE_FIELD_KEYS: Record<keyof SweepstakesFreeEntryFields, string> = {
  firstName: "field-1",
  lastName: "field-2",
  email: "field-3",
  phoneNumber: "field-4",
  address: "field-5",
  city: "field-6",
  postalCode: "field-7",
  country: "field-8",
  region: "field-9",
};

const getNoAutofillAttributes = (formId: string, field: keyof SweepstakesFreeEntryFields) => {
  const opaqueName = `${formId}-${OPAQUE_FIELD_KEYS[field]}`;
  return {
    id: opaqueName,
    inputProps: {
      autoComplete: "off",
      name: opaqueName,
      "data-1p-ignore": true,
      "data-lpignore": "true",
      "data-form-type": "other",
    },
  };
};

interface SweepstakesFreeEntryFormContentProps {
  form: UseFormReturn<SweepstakesFreeEntryFields>;
  formId?: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export const SweepstakesFreeEntryFormContent = ({
  form,
  formId = FREE_ENTRY_FORM_ID,
  onSubmit,
}: SweepstakesFreeEntryFormContentProps) => {
  const { t } = useTranslate();
  const country = form.watch("country");
  const { regionOptions } = useCountryRegion(country);
  const showsRegionPicker = !country || country === CountryNames.Canada || country === CountryNames.UnitedStates;

  return (
    <form id={formId} onSubmit={onSubmit} noValidate data-test="sweepstakes-free-entry-form">
      <Stack gap={2.5} sx={{ pt: 1 }}>
        <FormInfobox
          icon={<IdeaIcon />}
          components={{ icon: { size: "large" }, description: { sx: { typography: "subtitle2" } } }}
          description={t("ticketing", "sweepstakes.freeEntry.info")}
          sx={(theme) => ({
            padding: 2,
            ...(theme.palette.metadata?.mode === "dark" && { backgroundColor: theme.palette.forms.surface.supershy }),
          })}
        />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <FormTextField
              control={form.control}
              name="firstName"
              {...getNoAutofillAttributes(formId, "firstName")}
              label={t("ticketing", "firstName")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.firstNameRequired"))]}
              error={Boolean(form.formState.errors.firstName)}
              helperText={form.formState.errors.firstName?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField
              control={form.control}
              name="lastName"
              {...getNoAutofillAttributes(formId, "lastName")}
              label={t("ticketing", "lastName")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.lastNameRequired"))]}
              error={Boolean(form.formState.errors.lastName)}
              helperText={form.formState.errors.lastName?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={form.control}
              name="email"
              type="email"
              {...getNoAutofillAttributes(formId, "email")}
              label={t("ticketing", "sweepstakes.freeEntry.email")}
              rules={[
                isRequired(t("ticketing", "sweepstakes.freeEntry.errors.emailRequired")),
                isEmailAndAllowPlus(
                  t("ticketing", "sweepstakes.freeEntry.errors.emailRequired"),
                  t("common", "emailFormat.wrongChar")
                ),
              ]}
              error={Boolean(form.formState.errors.email)}
              helperText={form.formState.errors.email?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <FormPhoneInputField
              control={form.control}
              name="phoneNumber"
              id={getNoAutofillAttributes(formId, "phoneNumber").id}
              country="us"
              autoComplete="off"
              label={t("ticketing", "sweepstakes.freeEntry.phone")}
              rules={[
                isRequired(t("ticketing", "sweepstakes.freeEntry.errors.phoneRequired")),
                isPhoneNumberValid(t("ticketing", "sweepstakes.freeEntry.errors.phoneRequired")),
              ]}
              error={Boolean(form.formState.errors.phoneNumber)}
              helperText={form.formState.errors.phoneNumber?.message}
              data-test="sweepstakes-free-entry-phone"
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={form.control}
              name="address"
              {...getNoAutofillAttributes(formId, "address")}
              label={t("ticketing", "sweepstakes.freeEntry.address")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.addressRequired"))]}
              error={Boolean(form.formState.errors.address)}
              helperText={form.formState.errors.address?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField
              control={form.control}
              name="city"
              {...getNoAutofillAttributes(formId, "city")}
              label={t("ticketing", "sweepstakes.freeEntry.city")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.cityRequired"))]}
              error={Boolean(form.formState.errors.city)}
              helperText={form.formState.errors.city?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField
              control={form.control}
              name="postalCode"
              {...getNoAutofillAttributes(formId, "postalCode")}
              label={t("ticketing", "sweepstakes.freeEntry.postalCode")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.postalCodeRequired"))]}
              error={Boolean(form.formState.errors.postalCode)}
              helperText={form.formState.errors.postalCode?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSearchableSelectField
              control={form.control}
              name="country"
              id={getNoAutofillAttributes(formId, "country").id}
              options={COUNTRY_NAME_WITH_SHORT_CODE_OPTIONS}
              label={t("ticketing", "country")}
              placeholder={t("ticketing", "sweepstakes.freeEntry.selectCountry")}
              rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.countryRequired"))]}
              onChange={() => form.setValue("region", "")}
              error={Boolean(form.formState.errors.country)}
              helperText={form.formState.errors.country?.message}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {showsRegionPicker ? (
              <FormSearchableSelectField
                control={form.control}
                name="region"
                id={getNoAutofillAttributes(formId, "region").id}
                options={regionOptions}
                label={t("ticketing", "state")}
                placeholder={t("ticketing", "sweepstakes.freeEntry.selectState")}
                disabled={!country}
                rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.stateRequired"))]}
                error={Boolean(form.formState.errors.region)}
                helperText={form.formState.errors.region?.message}
              />
            ) : (
              <FormTextField
                control={form.control}
                name="region"
                {...getNoAutofillAttributes(formId, "region")}
                label={t("ticketing", "state")}
                rules={[isRequired(t("ticketing", "sweepstakes.freeEntry.errors.stateRequired"))]}
                error={Boolean(form.formState.errors.region)}
                helperText={form.formState.errors.region?.message}
              />
            )}
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
};
